import {Dialog} from 'platform/components';
import {Show} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';

import {TenantResponseBody} from '@dms/api/shared';
import {
  GetWarehouseResponse,
  ReceiveNoteState,
  usePatchReceiveNoteItemManualMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ControlledRowControl, type RowV5ResponseBody} from 'features/eag-grid';

import {ItemRowData} from '../types/ItemRowData';
import {ReceiveNoteItemDetail} from './ReceiveNoteItemDetail';

export type ReceiveNoteItemEditProps = {
  receiveNoteId: string;
  receiveNoteItemId: string;
  receiveNoteSupplierId: string | Nullish;
  dataQueryId: string;
  authorizationProfileId: string | Nullish;
  itemRowData: ItemRowData;
  warehouse: GetWarehouseResponse | Nullish;
  tenant: TenantResponseBody | Nullish;
  state: ReceiveNoteState | Nullish;
  isFromReceiveNote?: boolean;
  receiveNoteType?: string | Nullish;
  onClose: VoidFunction;
} & RequiredTestIdProps;

export function ReceiveNoteItemEdit(props: ReceiveNoteItemEditProps) {
  const [currentItemId, setCurrentItemId] = useState(props.receiveNoteItemId);

  const [, patchStatus] = usePatchReceiveNoteItemManualMutation({
    fixedCacheKey: 'edit-receive-note-item',
  });

  const isSavedByRowControlRef = useRef<boolean>(false);

  const formRef = useRef<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>(null);

  const isSavingChanges = patchStatus.isLoading;

  const handlePageChange = async (row: RowV5ResponseBody) => {
    const isDirty = formRef.current?.isDirty;
    const submitForm = formRef.current?.submit;

    if (!isDirty) {
      setCurrentItemId(row.id);
      return true;
    }

    isSavedByRowControlRef.current = true;

    const isSubmitSuccessful = await submitForm?.();

    if (!isSubmitSuccessful || patchStatus.isError) {
      isSavedByRowControlRef.current = false;
      return false;
    }

    setCurrentItemId(row.id);
    return true;
  };

  return (
    <Dialog
      title={i18n.t('general.labels.edit')}
      isOpen={isNotNil(props.receiveNoteItemId)}
      size="large"
      withAdditionalFooter
      onClose={props.onClose}
      pagination={
        <Show when={isNotNil(props.dataQueryId)}>
          <ControlledRowControl
            isDisabled={isSavingChanges}
            dataQueryId={props.dataQueryId}
            gridCode="receive-note-item"
            entityId={currentItemId}
            onPageChange={handlePageChange}
          />
        </Show>
      }
      data-testid={suffixTestId('dialogs.editItem', props)}
    >
      <ReceiveNoteItemDetail
        key={currentItemId}
        mode="editing"
        receiveNoteId={props.receiveNoteId}
        receiveNoteSupplierId={props.receiveNoteSupplierId}
        receiveNoteItemId={currentItemId}
        authorizationProfileId={props.authorizationProfileId}
        itemRowData={props.itemRowData}
        warehouse={props.warehouse}
        tenant={props.tenant}
        state={props.state}
        isFromReceiveNote={props.isFromReceiveNote}
        receiveNoteType={props.receiveNoteType}
        onClose={props.onClose}
        data-testid={suffixTestId('forms.editItem', props)}
      />
    </Dialog>
  );
}
